'use client';

import React, { useState, useMemo, useEffect, useRef } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import BookmarkButton from '@/components/BookmarkButton';
import { trackResonance, trackGlossarySearch } from '@/lib/bookmarks';
import { NAMES_DATA, SUNNAT_TIMELINES, NAME_CATEGORIES, type NameEntry, type SunnatTimeline,  } from '../names-reference/data/namesData';

// ─── TYPES ────────────────────────────────────────────────────────────────────

interface GlossaryTerm {
  id: string;
  term: string;
  arabic: string;
  root: string;
  rootMeaning: string;
  pronunciation: string;
  definition: string;
  etymology: string;
  quranicRoots: { ref: string; arabic: string; translation: string; href: string }[];
  crossLinks: { label: string; href: string; type: 'key' | 'ayat' | 'name' | 'phase' | 'lens' | 'mrg' | 'letter' }[];
  usageExamples: string[];
  relatedTerms: string[];
  category: 'framework' | 'irl' | 'letter' | 'name-concept';
  keyTag?: string;
  pageTag?: string;
}

interface PageIndexEntry {
  page: string;
  href: string;
  terms: string[];
  description: string;
  color: string;
}

// ─── GLOSSARY TERMS DATA ──────────────────────────────────────────────────────

const GLOSSARY_TERMS: GlossaryTerm[] = [
  {
    id: 'taqwah',
    term: 'Taqwah',
    arabic: 'تَقْوَى',
    root: 'و-ق-ي',
    rootMeaning: 'to protect, to guard, to shield',
    pronunciation: 'taq-wā',
    definition: 'God-consciousness — the quality of awareness that guards the self through recognition of Allah. Not fear alone, but a heightened state of perception that sees the divine in every encounter.',
    etymology: 'From the root و-ق-ي (w-q-y), meaning to protect or shield. Taqwah is the shield of consciousness — the quality that protects the caretaker from heedlessness (Ghaflah). The garment metaphor (Reshat Al-Taqwah) in Quran 7:26 frames it as clothing — something worn, not merely felt.',
    quranicRoots: [
      { ref: '2:2', arabic: 'هُدًى لِّلْمُتَّقِينَ', translation: 'Guidance for those of Taqwah', href: 'https://quran.com/2/2' },
      { ref: '7:26', arabic: 'وَلِبَاسُ التَّقْوَىٰ ذَٰلِكَ خَيْرٌ', translation: 'And the garment of Taqwah — that is best', href: 'https://quran.com/7/26' },
      { ref: '49:13', arabic: 'إِنَّ أَكْرَمَكُمْ عِندَ اللَّهِ أَتْقَاكُمْ', translation: 'The most honored of you in the sight of Allah is the most conscious', href: 'https://quran.com/49/13' },
    ],
    crossLinks: [
      { label: 'Key 03: Taqwah', href: '/keys-to-kulli-haal', type: 'key' },
      { label: 'Names: Al-Muhaymin', href: '/sl-glossary', type: 'name' },
      { label: 'Ayat in Creation', href: '/ayat-in-creation', type: 'ayat' },
      { label: 'Open Letter §3', href: '/open-letter', type: 'letter' },
    ],
    usageExamples: [
      'The caretaker with Taqwah sees the vacant lot not as blight but as an Ayah — a sign of Al-Muhyi waiting to be recognized.',
      'Taqwah is the quality that makes the IRL Lens work — without it, the lens has no operator.',
      'The Taqwah → Ihsan gradient: from awareness of Allah to excellence in action as if you see Him.',
    ],
    relatedTerms: ['Ihsan', 'Yaqeen', 'Furqan', 'Fitra', 'Reshat Al-Taqwah'],
    category: 'framework',
    keyTag: 'Key 03',
    pageTag: 'Keys to Kulli Haal',
  },
  {
    id: 'ihsan',
    term: 'Ihsan',
    arabic: 'إِحْسَان',
    root: 'ح-س-ن',
    rootMeaning: 'to be beautiful, to be good, to perfect',
    pronunciation: 'ih-sān',
    definition: 'Excellence — the highest degree of God-consciousness in work. To worship Allah as if you see Him, and if you cannot see Him, to know that He sees you. The closing concept of the Taqwah → Ihsan gradient.',
    etymology: 'From the root ح-س-ن (h-s-n), meaning beauty and goodness. Ihsan is the beautification of action — doing what is good in the most excellent way. The famous hadith of Jibril defines it as the third level of the deen, beyond Islam and Iman.',
    quranicRoots: [
      { ref: '16:90', arabic: 'إِنَّ اللَّهَ يَأْمُرُ بِالْعَدْلِ وَالْإِحْسَانِ', translation: 'Indeed, Allah commands Al-Adl (justice) and Ihsan (excellence)', href: 'https://quran.com/16/90' },
      { ref: '55:60', arabic: 'هَلْ جَزَاءُ الْإِحْسَانِ إِلَّا الْإِحْسَانُ', translation: 'Is the reward of Ihsan anything but Ihsan?', href: 'https://quran.com/55/60' },
    ],
    crossLinks: [
      { label: 'Key 03: Taqwah→Ihsan Gradient', href: '/keys-to-kulli-haal', type: 'key' },
      { label: 'Names: Al-Muhsin', href: '/sl-glossary', type: 'name' },
      { label: 'Open Letter §29', href: '/open-letter', type: 'letter' },
    ],
    usageExamples: [
      'Ihsan in caretaking: the community garden tended with the awareness that Al-Basir sees every act of cultivation.',
      'The Taqwah → Ihsan gradient is a quality spectrum — not a binary switch but a continuous deepening of consciousness.',
      'Ihsan is the fruit of the recognition arc — when Uboor is reached, the caretaker acts with Ihsan.',
    ],
    relatedTerms: ['Taqwah', 'Yaqeen', 'Amanah', 'Falah'],
    category: 'framework',
    keyTag: 'Key 03',
    pageTag: 'Keys to Kulli Haal',
  },
  {
    id: 'wujud',
    term: 'Wujud',
    arabic: 'وُجُود',
    root: 'و-ج-د',
    rootMeaning: 'to find, to exist, to be present',
    pronunciation: 'wu-jūd',
    definition: 'Existence — the foundational recognition that everything that exists points to Al-Wajid (The Finder/The Existing). Wujud is the ontological ground of the entire framework.',
    etymology: 'From the root و-ج-د (w-j-d), meaning to find or to be present. Al-Wajid is the Name of Allah that means He who finds — the One whose existence is self-evident and necessary. Wujud in the framework means: the existence of any created thing is itself an Ayah pointing to the Necessary Existent.',
    quranicRoots: [
      { ref: '2:115', arabic: 'فَأَيْنَمَا تُوَلُّوا فَثَمَّ وَجْهُ اللَّهِ', translation: 'Wherever you turn, there is the Face of Allah', href: 'https://quran.com/2/115' },
      { ref: '41:53', arabic: 'سَنُرِيهِمْ آيَاتِنَا فِي الْآفَاقِ وَفِي أَنفُسِهِمْ', translation: 'We will show them Our signs in the horizons and in themselves', href: 'https://quran.com/41/53' },
    ],
    crossLinks: [
      { label: 'Key 01: Ayat (Wujud)', href: '/keys-to-kulli-haal', type: 'key' },
      { label: 'Names: Al-Wajid', href: '/sl-glossary', type: 'name' },
      { label: 'Ayat in Creation', href: '/ayat-in-creation', type: 'ayat' },
      { label: 'IRL Lens', href: '/inner-reality-lens', type: 'lens' },
    ],
    usageExamples: [
      'Wujud is the starting point of the IRL Lens: before asking "What is it?", the caretaker confirms "Is it?" — the first act of recognizing existence.',
      'Every Ayah in creation is a proof of Wujud — the existence of the sign points to the existence of the Signifier.',
      'Al-Wajid as the anchor Name for Key 01: the One who finds is the One whose existence makes all finding possible.',
    ],
    relatedTerms: ['Ayah', 'Fitra', 'Taqwah', 'Yaqeen', 'Huduth Dhati'],
    category: 'framework',
    keyTag: 'Key 01',
    pageTag: 'Keys to Kulli Haal',
  },
  {
    id: 'furqan',
    term: 'Furqan',
    arabic: 'فُرْقَان',
    root: 'ف-ر-ق',
    rootMeaning: 'to separate, to distinguish, to divide',
    pronunciation: 'fur-qān',
    definition: 'Discernment — the Quran as the ultimate tool for distinguishing truth from falsehood. Furqan is the criterion, the separator, the divine standard of discernment.',
    etymology: 'From the root ف-ر-ق (f-r-q), meaning to separate or distinguish. Al-Furqan is one of the names of the Quran — the Criterion that separates truth from falsehood. In the framework, Furqan is the sub-concept under Key 03 (Taqwah): the quality of discernment that Taqwah produces.',
    quranicRoots: [
      { ref: '2:185', arabic: 'شَهْرُ رَمَضَانَ الَّذِي أُنزِلَ فِيهِ الْقُرْآنُ هُدًى لِّلنَّاسِ وَبَيِّنَاتٍ مِّنَ الْهُدَىٰ وَالْفُرْقَانِ', translation: 'The month of Ramadan in which the Quran was revealed — guidance and clear proofs and the Furqan', href: 'https://quran.com/2/185' },
      { ref: '8:29', arabic: 'إِن تَتَّقُوا اللَّهَ يَجْعَل لَّكُمْ فُرْقَانًا', translation: 'If you fear Allah, He will grant you a Furqan (criterion)', href: 'https://quran.com/8/29' },
    ],
    crossLinks: [
      { label: 'Key 03: Furqan Sub-Concept', href: '/keys-to-kulli-haal', type: 'key' },
      { label: 'Names: Al-Hakam', href: '/sl-glossary', type: 'name' },
    ],
    usageExamples: [
      'Furqan in the IRL Lens: the ability to distinguish an Ayah from mere coincidence — the criterion of recognition.',
      'The Quran as Furqan: every verse is a separator between the apparent and the real, the Zahir and the Batin.',
      'Taqwah produces Furqan: the more God-conscious the caretaker, the sharper their discernment.',
    ],
    relatedTerms: ['Taqwah', 'Basirah', 'Yaqeen', 'Haqq'],
    category: 'framework',
    keyTag: 'Key 03',
    pageTag: 'Keys to Kulli Haal',
  },
  {
    id: 'amanah',
    term: 'Amanah',
    arabic: 'أَمَانَة',
    root: 'أ-م-ن',
    rootMeaning: 'to be safe, to trust, to be faithful',
    pronunciation: 'amā-nah',
    definition: 'Trust — the sacred responsibility the caretaker carries. The Amanah is the trust of creation: the caretaker holds the world in trust, not in ownership.',
    etymology: 'From the root أ-م-ن (a-m-n), the same root as Iman (faith) and Aman (safety). Amanah is the trust that Allah offered to the heavens, the earth, and the mountains — and which the human being accepted (33:72). The "loom on loan" metaphor: the caretaker is a weaver who did not make the loom.',
    quranicRoots: [
      { ref: '33:72', arabic: 'إِنَّا عَرَضْنَا الْأَمَانَةَ عَلَى السَّمَاوَاتِ وَالْأَرْضِ', translation: 'We offered the Amanah to the heavens and the earth and the mountains', href: 'https://quran.com/33/72' },
      { ref: '4:58', arabic: 'إِنَّ اللَّهَ يَأْمُرُكُمْ أَن تُؤَدُّوا الْأَمَانَاتِ إِلَىٰ أَهْلِهَا', translation: 'Indeed, Allah commands you to render trusts to their rightful owners', href: 'https://quran.com/4/58' },
    ],
    crossLinks: [
      { label: 'Key 05: Amanah (Loom on Loan)', href: '/keys-to-kulli-haal', type: 'key' },
      { label: 'Muslim Resource Guide', href: '/muslim-resource-guide', type: 'mrg' },
      { label: 'Open Letter §29', href: '/open-letter', type: 'letter' },
    ],
    usageExamples: [
      'The loom on loan: the caretaker\'s skills, time, and capacity are not owned — they are held in trust for the community.',
      'Haqq Al-Amanah: every resource in the Muslim Resource Guide is an act of fulfilling the trust, not charity.',
      'Amanah as the closing stage of the recognition arc: after Uboor, the caretaker acts from a place of trust, not ownership.',
    ],
    relatedTerms: ['Falah', 'Islah', 'Haqq', 'Khalifa'],
    category: 'framework',
    keyTag: 'Key 05',
    pageTag: 'Keys to Kulli Haal',
  },
  {
    id: 'falah',
    term: 'Falah',
    arabic: 'فَلَاح',
    root: 'ف-ل-ح',
    rootMeaning: 'to split the earth, to cultivate, to succeed',
    pronunciation: 'fa-lāh',
    definition: 'Flourishing — the reframing of success as cultivation, not accumulation. Falah is the farmer\'s success: patient, rooted, generational. Hayya ala al-Falah — come to flourishing.',
    etymology: 'From the root ف-ل-ح (f-l-h), meaning to split the earth for cultivation. The farmer who splits the earth to plant is the original image of Falah. In the Adhan, "Hayya ala al-Falah" (come to flourishing) is the call to the highest success — not wealth or status, but the cultivation of what endures.',
    quranicRoots: [
      { ref: '2:5', arabic: 'أُولَٰئِكَ عَلَىٰ هُدًى مِّن رَّبِّهِمْ ۖ وَأُولَٰئِكَ هُمُ الْمُفْلِحُونَ', translation: 'Those are upon guidance from their Lord, and those are the successful (Muflihun)', href: 'https://quran.com/2/5' },
    ],
    crossLinks: [
      { label: 'Key 06: Falah', href: '/keys-to-kulli-haal', type: 'key' },
      { label: 'Ayat in Creation', href: '/ayat-in-creation', type: 'ayat' },
    ],
    usageExamples: [
      'Falah vs. Najaah: Falah is cultivation (the farmer), Najaah is achievement (the competitor). The framework chooses Falah.',
      'The Longleaf Pine as Falah: a tree that takes 100 years to mature, providing for generations it will never see.',
      'Al-Asr four conditions for Falah: Iman, righteous deeds, mutual counsel of truth, mutual counsel of patience.',
    ],
    relatedTerms: ['Yaqeen', 'Amanah', 'Ihsan', 'Islah'],
    category: 'framework',
    keyTag: 'Key 06',
    pageTag: 'Keys to Kulli Haal',
  },
  {
    id: 'yaqeen',
    term: 'Yaqeen',
    arabic: 'يَقِين',
    root: 'ي-ق-ن',
    rootMeaning: 'to be certain, to be settled, to be firm',
    pronunciation: 'ya-qīn',
    definition: 'Certainty — the fruit of aligned Basar (outer sight) and Basirah (inner sight). Three levels: Ilm al-Yaqeen (knowledge certainty), Ayn al-Yaqeen (perceptual certainty), Haqq al-Yaqeen (reality certainty).',
    etymology: 'From the root ي-ق-ن (y-q-n), meaning to be settled and certain. The IRL Lens moves the caretaker from Ilm to Ayn to Haqq. The platform is a Mata for Ilm and Ayn Yaqeen — Haqq Yaqeen requires direct encounter with creation.',
    quranicRoots: [
      { ref: '102:5-7', arabic: 'كَلَّا لَوْ تَعْلَمُونَ عِلْمَ الْيَقِينِ', translation: 'If you knew with knowledge of certainty', href: 'https://quran.com/102/5' },
      { ref: '51:20', arabic: 'وَفِي الْأَرْضِ آيَاتٌ لِّلْمُوقِنِينَ', translation: 'And in the earth are signs for those of Yaqeen', href: 'https://quran.com/51/20' },
    ],
    crossLinks: [
      { label: 'Key 07: Yaqeen', href: '/keys-to-kulli-haal', type: 'key' },
      { label: 'IRL Lens', href: '/inner-reality-lens', type: 'lens' },
    ],
    usageExamples: [
      'Ilm al-Yaqeen: the caretaker reads about the Names of Allah in creation — the first level.',
      'Ayn al-Yaqeen: the caretaker sees evidence — photographs, data, other caretakers\' observations.',
      'Haqq al-Yaqeen: the caretaker stands in the ward, holds the leaf, serves the community.',
    ],
    relatedTerms: ['Taqwah', 'Furqan', 'Basirah', 'Wujud'],
    category: 'framework',
    keyTag: 'Key 07',
    pageTag: 'Keys to Kulli Haal',
  },
  {
    id: 'khalifa',
    term: 'Khalifa / Khilafah',
    arabic: 'خَلِيفَة / خِلَافَة',
    root: 'خ-ل-ف',
    rootMeaning: 'to succeed, to come after, to be a steward',
    pronunciation: 'kha-lī-fah / khi-lā-fah',
    definition: 'Steward/Stewardship — the human being as the appointed steward of creation. Khalifa is not ruler or king — it is the one who comes after, who tends what was entrusted.',
    etymology: 'From the root خ-ل-ف (kh-l-f), meaning to come after or succeed. Quran 2:30 establishes this: "I am placing a Khalifa in the earth." The human being is not the owner of creation — they are the appointed steward.',
    quranicRoots: [
      { ref: '2:30', arabic: 'إِنِّي جَاعِلٌ فِي الْأَرْضِ خَلِيفَةً', translation: 'Indeed, I am placing a Khalifa (steward) in the earth', href: 'https://quran.com/2/30' },
      { ref: '6:165', arabic: 'وَهُوَ الَّذِي جَعَلَكُمْ خَلَائِفَ الْأَرْضِ', translation: 'And it is He who made you Khalifa (stewards) of the earth', href: 'https://quran.com/6/165' },
    ],
    crossLinks: [
      { label: 'Key 05: Amanah', href: '/keys-to-kulli-haal', type: 'key' },
      { label: 'Muslim Resource Guide', href: '/muslim-resource-guide', type: 'mrg' },
      { label: 'Open Letter §29', href: '/open-letter', type: 'letter' },
    ],
    usageExamples: [
      'The Khalifa does not own the land — they tend it. The MRG is a map of Khilafah.',
      'Khilafah reframes environmental work: the caretaker is not an activist — they are a steward fulfilling their appointed role.',
      'The loom on loan: the Khalifa uses the tools of creation without claiming ownership.',
    ],
    relatedTerms: ['Amanah', 'Islah', 'Haqq', 'Falah', 'Deen'],
    category: 'letter',
    pageTag: 'Open Letter',
  },
  {
    id: 'mizan',
    term: 'Mizan',
    arabic: 'مِيزَان',
    root: 'و-ز-ن',
    rootMeaning: 'to weigh, to balance, to measure',
    pronunciation: 'mī-zān',
    definition: 'The Balance/Scale — the divine measure of justice and proportion in creation. Mizan is the cosmic scale by which all things are weighed.',
    etymology: 'From the root و-ز-ن (w-z-n), meaning to weigh or balance. The Mizan appears in Surah Ar-Rahman as the cosmic scale: "And the heaven He raised and imposed the balance (Mizan), that you not transgress within the balance." (55:7-8)',
    quranicRoots: [
      { ref: '55:7-9', arabic: 'وَالسَّمَاءَ رَفَعَهَا وَوَضَعَ الْمِيزَانَ', translation: 'And the heaven He raised and imposed the Mizan', href: 'https://quran.com/55/7' },
    ],
    crossLinks: [
      { label: 'Key 04: Mizan', href: '/keys-to-kulli-haal', type: 'key' },
      { label: 'Muslim Resource Guide', href: '/muslim-resource-guide', type: 'mrg' },
    ],
    usageExamples: [
      'The Mizan in the MRG: when a community lacks housing, healthcare, or education, the Mizan is out of balance.',
      'Surah Ar-Rahman frames the Mizan as the principle of creation — not just judgment.',
      'Al-Adl is the Name of Allah active in the Mizan — the Just who weighs all things with precision.',
    ],
    relatedTerms: ['Haqq', 'Islah', 'Amanah', 'Khalifa', 'Falah'],
    category: 'letter',
    keyTag: 'Key 04',
    pageTag: 'Keys to Kulli Haal',
  },
  {
    id: 'islah',
    term: 'Islah',
    arabic: 'إِصْلَاح',
    root: 'ص-ل-ح',
    rootMeaning: 'to set right, to repair, to make good',
    pronunciation: 'is-lāh',
    definition: 'Reform/Setting Right — the act of restoring balance, repairing what is broken, making good what has gone wrong. Islah shares its root with Salah (prayer).',
    etymology: 'From the root ص-ل-ح (s-l-h), the same root as Salah (prayer), Sulh (peace/reconciliation), and Salih (righteous). Islah is not reform in the political sense — it is the act of setting things right in alignment with the Mizan.',
    quranicRoots: [
      { ref: '11:88', arabic: 'إِنْ أُرِيدُ إِلَّا الْإِصْلَاحَ مَا اسْتَطَعْتُ', translation: 'I only intend Islah (reform/setting right) as much as I am able', href: 'https://quran.com/11/88' },
      { ref: '7:56', arabic: 'وَلَا تُفْسِدُوا فِي الْأَرْضِ بَعْدَ إِصْلَاحِهَا', translation: 'And do not cause corruption in the earth after its Islah', href: 'https://quran.com/7/56' },
    ],
    crossLinks: [
      { label: 'Muslim Resource Guide', href: '/muslim-resource-guide', type: 'mrg' },
      { label: 'Key 05: Amanah', href: '/keys-to-kulli-haal', type: 'key' },
      { label: 'IRL Lens: Uboor', href: '/inner-reality-lens', type: 'lens' },
    ],
    usageExamples: [
      'Islah = Salah: the act of setting things right is rooted in the same root as prayer.',
      'The MRG is a map of Islah: every resource in the guide is an act of setting things right.',
      'Uboor leads to Islah: the crossing from recognition to action is the crossing from seeing the imbalance to working to restore the Mizan.',
    ],
    relatedTerms: ['Mizan', 'Haqq', 'Amanah', 'Khalifa', 'Falah', 'Salam'],
    category: 'letter',
    pageTag: 'Open Letter',
  },
  {
    id: 'uboor',
    term: 'Uboor',
    arabic: 'عُبُور',
    root: 'ع-ب-ر',
    rootMeaning: 'to cross over, to pass through, to interpret',
    pronunciation: 'u-boor',
    definition: 'Crossing — the final stage of the Recognition Arc. Uboor is the crossing-over that happens when recognition becomes action — when the caretaker moves from seeing to being.',
    etymology: 'From the root ع-ب-ر (a-b-r), meaning to cross or pass through. Uboor is the crossing — the moment when the recognition arc completes and the caretaker crosses from awareness to action.',
    quranicRoots: [
      { ref: '2:249', arabic: 'فَلَمَّا جَاوَزَهُ هُوَ وَالَّذِينَ آمَنُوا مَعَهُ', translation: 'But when he had crossed it, he and those who believed with him', href: 'https://quran.com/2/249' },
    ],
    crossLinks: [
      { label: 'IRL Lens', href: '/inner-reality-lens', type: 'lens' },
      { label: 'Guided Walkthrough', href: '/guided-walkthrough', type: 'phase' },
    ],
    usageExamples: [
      'Uboor is the closing stage of the Recognition Arc: Mata → Encounter → Examination → Recognition → Re-Assembly → Uboor.',
      'The IRL Lens closing meditation: Uboor as the crossing from recognition to action.',
      'Yaqeen enables Uboor: the certainty that comes from having seen the signs makes the crossing possible.',
    ],
    relatedTerms: ['Yaqeen', 'Haqq', 'Amanah', 'Falah'],
    category: 'irl',
    pageTag: 'Inner Reality Lens',
  },
  {
    id: 'haqq',
    term: 'Haqq',
    arabic: 'حَقّ',
    root: 'ح-ق-ق',
    rootMeaning: 'to be true, to be right, to be due',
    pronunciation: 'haqq',
    definition: 'Truth/Right/Due — the most comprehensive term in the framework. Haqq is simultaneously: the truth of a thing, the right it carries, and what is due to it. Al-Haqq is a Name of Allah — the Real, the True.',
    etymology: 'From the root ح-ق-ق (h-q-q), meaning to be true, right, or established. Haqq has three simultaneous meanings: (1) Truth — what is real and established; (2) Right — what is owed or due; (3) The Real — Al-Haqq as a Name of Allah.',
    quranicRoots: [
      { ref: '22:6', arabic: 'ذَٰلِكَ بِأَنَّ اللَّهَ هُوَ الْحَقُّ', translation: 'That is because Allah — He is Al-Haqq (The Real/True)', href: 'https://quran.com/22/6' },
      { ref: '17:81', arabic: 'وَقُلْ جَاءَ الْحَقُّ وَزَهَقَ الْبَاطِلُ', translation: 'And say: Al-Haqq has come and falsehood has departed', href: 'https://quran.com/17/81' },
    ],
    crossLinks: [
      { label: 'IRL Lens: Layer 6', href: '/inner-reality-lens', type: 'lens' },
      { label: 'Muslim Resource Guide', href: '/muslim-resource-guide', type: 'mrg' },
    ],
    usageExamples: [
      'Haqq Al-Khalq: the right of creation — every created thing carries rights by virtue of being created.',
      'IRL Lens Layer 6 — the Haqq statement: "What is true, right, and due in what I observe?"',
      'Al-Haqq as a Name: the caretaker who recognizes Al-Haqq in creation cannot remain passive.',
    ],
    relatedTerms: ['Mizan', 'Amanah', 'Islah', 'Yaqeen', 'Khalifa'],
    category: 'letter',
    pageTag: 'Open Letter',
  },
  {
    id: 'rahman-rahim-root',
    term: 'Ar-Rahman / Ar-Rahim — Root Analysis',
    arabic: 'الرَّحْمَٰن / الرَّحِيم',
    root: 'ر-ح-م',
    rootMeaning: 'womb, mercy, compassion, tenderness',
    pronunciation: 'ar-rah-mān / ar-ra-hīm',
    definition: 'Both Names derive from the root ر-ح-م (r-h-m) — the womb. Ar-Rahman is the vast, encompassing mercy that covers all creation without distinction. Ar-Rahim is the specific, targeted mercy directed toward the believers.',
    etymology: 'The root ر-ح-م (r-h-m) is the word for womb (Rahim). From this root: Ar-Rahman (the vast mercy that encompasses all creation, like the womb that holds without condition), Ar-Rahim (the specific mercy directed with intention, like the care of a mother for her child). The Quran opens with both Names together — the Basmala.',
    quranicRoots: [
      { ref: '1:1', arabic: 'بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ', translation: 'In the name of Allah, Ar-Rahman, Ar-Rahim', href: 'https://quran.com/1/1' },
      { ref: '55:1-2', arabic: 'الرَّحْمَٰنُ ۝ عَلَّمَ الْقُرْآنَ', translation: 'Ar-Rahman — He taught the Quran', href: 'https://quran.com/55/1' },
    ],
    crossLinks: [
      { label: 'Keys to Kulli Haal', href: '/keys-to-kulli-haal', type: 'key' },
      { label: 'Open Letter §29', href: '/open-letter', type: 'letter' },
    ],
    usageExamples: [
      'Ar-Rahman is the mercy that falls as rain on the just and unjust alike — the encompassing mercy of creation itself.',
      'Ar-Rahim is the mercy that responds to the believer\'s turning — the specific, targeted mercy that the caretaker seeks.',
      'The Basmala opens every action with both Names: the caretaker acts within the encompassing mercy (Rahman) and seeks the specific mercy (Rahim).',
    ],
    relatedTerms: ['Taqwah', 'Ihsan', 'Amanah', 'Deen'],
    category: 'name-concept',
    pageTag: 'Names Reference',
  },
  {
    id: 'khaliq-bari-musawwir',
    term: 'Al-Khaliq / Al-Bari / Al-Musawwir — Creation Triad',
    arabic: 'الْخَالِق / الْبَارِئ / الْمُصَوِّر',
    root: 'خ-ل-ق / ب-ر-أ / ص-و-ر',
    rootMeaning: 'to create / to originate forms / to fashion shapes',
    pronunciation: 'al-khā-liq / al-bā-ri / al-mu-saw-wir',
    definition: 'The Creation Triad — three Names of Allah that appear together in Quran 59:24, each describing a distinct aspect of the creative act. Al-Khaliq creates from nothing; Al-Bari originates the essential natures of things; Al-Musawwir fashions the specific forms.',
    etymology: 'Three roots, three aspects of creation: Al-Khaliq (خ-ل-ق) — the act of creating; Al-Bari (ب-ر-أ) — the origination of essential natures; Al-Musawwir (ص-و-ر) — the fashioning of specific forms. The Leaf Sequence begins with this triad.',
    quranicRoots: [
      { ref: '59:24', arabic: 'هُوَ اللَّهُ الْخَالِقُ الْبَارِئُ الْمُصَوِّرُ ۖ لَهُ الْأَسْمَاءُ الْحُسْنَىٰ', translation: 'He is Allah — Al-Khaliq, Al-Bari, Al-Musawwir. To Him belong the most beautiful Names.', href: 'https://quran.com/59/24' },
    ],
    crossLinks: [
      { label: 'Ayat in Creation', href: '/ayat-in-creation', type: 'ayat' },
      { label: 'IRL Lens: Leaf Sequence', href: '/inner-reality-lens', type: 'lens' },
    ],
    usageExamples: [
      'The Leaf Sequence reads the Creation Triad: the leaf\'s existence (Khaliq) → its essential nature (Bari) → its specific form (Musawwir).',
      'Al-Musawwir is the Name active in Taqdir — the specific form of every created thing is a divine measurement.',
      'The IRL Lens Three Questions map to the Creation Triad: "Is it?" (Khaliq) → "What is it?" (Bari) → "When is it?" (Musawwir/Taqdir).',
    ],
    relatedTerms: ['Wujud', 'Mahiyya', 'Huduth Dhati', 'Qadr', 'Ayah'],
    category: 'name-concept',
    pageTag: 'Names Reference',
  },
  {
    id: 'zahir-batin-pair',
    term: 'Az-Zahir / Al-Batin — The Manifest & Hidden',
    arabic: 'الظَّاهِر / الْبَاطِن',
    root: 'ظ-ه-ر / ب-ط-ن',
    rootMeaning: 'to be manifest, to appear / to be hidden, to be inward',
    pronunciation: 'az-zā-hir / al-bā-tin',
    definition: 'The paired Names of manifestation and hiddenness — the axis of the IRL Lens. Az-Zahir is the outwardly apparent; Al-Batin is the inwardly concealed. Both appear together in Quran 57:3 alongside Al-Awwal and Al-Akhir.',
    etymology: 'Az-Zahir from the root ظ-ه-ر (z-h-r): to be manifest, to appear. Al-Batin from the root ب-ط-ن (b-t-n): to be hidden, to be inward. The IRL Lens is built on this axis: every Ayah has a zahir (visible sign) and a batin (hidden meaning). The caretaker descends from zahir to batin through the 8-layer depth panel.',
    quranicRoots: [
      { ref: '57:3', arabic: 'هُوَ الْأَوَّلُ وَالْآخِرُ وَالظَّاهِرُ وَالْبَاطِنُ', translation: 'He is the First and the Last, the Manifest and the Hidden', href: 'https://quran.com/57/3' },
    ],
    crossLinks: [
      { label: 'IRL Lens: Zahir-Batin Axis', href: '/inner-reality-lens', type: 'lens' },
      { label: 'Key 01: AYAT', href: '/keys-to-kulli-haal', type: 'key' },
    ],
    usageExamples: [
      'The Big Bluestem grass: the visible grass above ground (Zahir) — the root system 4x deeper than what is seen (Batin).',
      'The Mycorrhizal Network: the visible forest above (Zahir) — the underground fungal network (Batin).',
      'The IRL Lens moves from Zahir to Batin — the Batin is the threshold of the Ghayb.',
    ],
    relatedTerms: ['Ayah', 'Ghayb', 'Basirah', 'Haqq', 'Wujud'],
    category: 'name-concept',
    pageTag: 'Names Reference',
  },
  {
    id: 'adl-qist-root',
    term: 'Al-Adl / Al-Qist — Justice Names',
    arabic: 'الْعَدْل / الْقِسْط',
    root: 'ع-د-ل',
    rootMeaning: 'to be straight, to be equal, to be just',
    pronunciation: 'al-adl / al-qist',
    definition: 'Al-Adl is the Name of Allah as The Just — the One whose justice is intrinsic to His nature. Al-Qist is the justice that is distributed — the equity that the caretaker works to restore. Both are active in the Mizan.',
    etymology: 'Al-Adl comes from the root ع-د-ل (a-d-l), meaning to be straight or equal. Al-Qist (القِسْط) comes from the root ق-س-ط, meaning to distribute equitably. The distinction: Al-Adl is justice as an attribute of Allah; Al-Qist is justice as a practice in the world.',
    quranicRoots: [
      { ref: '16:90', arabic: 'إِنَّ اللَّهَ يَأْمُرُ بِالْعَدْلِ وَالْإِحْسَانِ', translation: 'Indeed, Allah commands Al-Adl (justice) and Ihsan (excellence)', href: 'https://quran.com/16/90' },
      { ref: '57:25', arabic: 'لِيَقُومَ النَّاسُ بِالْقِسْطِ', translation: 'That people may maintain Al-Qist (equity/justice)', href: 'https://quran.com/57/25' },
    ],
    crossLinks: [
      { label: 'Muslim Resource Guide', href: '/muslim-resource-guide', type: 'mrg' },
      { label: 'Keys: Mizan', href: '/keys-to-kulli-haal', type: 'key' },
    ],
    usageExamples: [
      'Al-Adl is the Name active in the Mizan — the Just who weighs all things.',
      'Al-Qist in the MRG: the distribution of resources in alignment with the Mizan.',
      'The Justice Thread pathway: Al-Adl → Al-Haqq → Mizan → MRG → Community Action.',
    ],
    relatedTerms: ['Mizan', 'Haqq', 'Islah', 'Amanah', 'Khalifa'],
    category: 'name-concept',
    pageTag: 'Names Reference',
  },
  {
    id: 'ilm',
    term: 'Ilm',
    arabic: 'عِلْم',
    root: 'ع-ل-م',
    rootMeaning: 'to have knowledge · to be cognizant · to be certain · to be aware · to gather information · to have intuitive knowledge · to be firmly rooted in knowledge',
    pronunciation: 'ilm',
    definition: 'Knowledge — the primary Islamic term for knowledge in all its forms: acquired, intuitive, certain, and divine. Ilm is the foundation of the IRL Lens methodology: every observation is an act of seeking Ilm.',
    etymology: 'From the root ع-ل-م (Ein-Lam-Mim), the same root as Al-Alim (The All-Knowing) and Ulema (scholars). The root does not mean merely "to know" — it contains a meaning field: to be cognizant, to be certain, to gather information, to have intuitive knowledge, to be firmly rooted.',
    quranicRoots: [
      { ref: '65:12', arabic: 'وَأَنَّ اللَّهَ قَدْ أَحَاطَ بِكُلِّ شَيْءٍ عِلْمًا', translation: 'And that Allah encompasses all things in knowledge', href: 'https://quran.com/65/12' },
      { ref: '2:31', arabic: 'وَعَلَّمَ آدَمَ الْأَسْمَاءَ كُلَّهَا', translation: 'And He taught Adam the names — all of them', href: 'https://quran.com/2/31' },
    ],
    crossLinks: [
      { label: 'Open Letter — Ilm Section', href: '/open-letter', type: 'letter' },
      { label: 'IRL Lens — Ilm Dock', href: '/inner-reality-lens', type: 'lens' },
      { label: 'Keys to Kulli Haal', href: '/keys-to-kulli-haal', type: 'key' },
    ],
    usageExamples: [
      'The three levels of Ilm: Ilm al-Yaqeen (knowledge by information), Ayn al-Yaqeen (knowledge by direct sight), Haqq al-Yaqeen (knowledge by complete immersion).',
      'The Rain Hadith maps three soil types to three responses to Ilm.',
      'Al-Alim taught Adam the names of all things (2:31) — the IRL Lens exercises this Adamic capacity.',
    ],
    relatedTerms: ['Yaqeen', 'Aql', 'Fikr', 'Al-Alim'],
    category: 'letter',
    pageTag: 'Open Letter',
  },
];

// ─── PAGE INDEX DATA ──────────────────────────────────────────────────────────

const PAGE_INDEX: PageIndexEntry[] = [
  {
    page: 'Open Letter',
    href: '/open-letter',
    color: '#D4A82A',
    description: 'The foundation document — all SL terms originate here. Section 29 is the Sacred Language Glossary.',
    terms: ['Ayah', 'Haal', 'Zahir/Batin', 'Hawas', 'Dunyah', 'Fikr', 'Ayn', 'Aql', 'Ilm', 'Dikr', 'Basirah', 'Hikmah', 'Wujud', 'Mahiyya', 'Fitra', 'Haqq', 'Yaqeen', 'Sunnat Allah', 'Deen', 'Amanah', 'Khalifa', 'Ihsan', 'Islah', 'Mizan'],
  },
  {
    page: 'Keys to Kulli Haal',
    href: '/keys-to-kulli-haal',
    color: '#D4A82A',
    description: 'The curriculum framework — seven Keys built from SL roots.',
    terms: ['Wujud (Key 01)', 'Fitra (Key 01)', 'Haal (Key 02)', 'Taqwah (Key 03)', 'Reshat Al-Taqwah (Key 03)', 'Furqan (Key 03)', 'Ihsan (Key 03)', 'Mizan (Key 04)', 'Amanah (Key 05)', 'Falah (Key 06)', 'Yaqeen (Key 07)', 'Ihdini'],
  },
  {
    page: 'Inner Reality Lens',
    href: '/inner-reality-lens',
    color: '#2d8a87',
    description: 'The methodological instrument — 8 layers of recognition, each with its own SL vocabulary.',
    terms: ['Zahir (Layer 1)', 'Wujud/Huduth (Layer 2)', 'Mahiyya (Layer 3)', 'Sunnat Allah/Amr (Layer 4)', 'Zahir-Batin (Layer 5/6)', 'Haqq (Layer 6)', 'Amanah (Layer 7)', 'Uboor', 'Basirah', 'Fikr', 'Haal'],
  },
  {
    page: 'Ayat in Creation',
    href: '/ayat-in-creation',
    color: '#2d7a47',
    description: 'The open-world field — reading Ayat in ecosystems, communities, and creation.',
    terms: ['Ayah', 'Zahir/Batin', 'Hawas', 'Dunyah', 'Fikr', 'Wujud', 'Taqwah'],
  },
  {
    page: 'Names Reference (Integrated)',
    href: '/sl-glossary',
    color: '#8a8ab8',
    description: 'The Names of Allah — fully integrated into this glossary. Each Name is a door of Dikr and a lens for reading creation.',
    terms: ['Al-Khaliq', 'Al-Bari', 'Al-Musawwir', 'Az-Zahir', 'Al-Batin', 'Ar-Rahman', 'Al-Qadir', 'Ar-Razzaq', 'An-Nur', 'Al-Haqq', 'Al-Wajid', 'Dikr', 'Hikmah'],
  },
  {
    page: 'Muslim Resource Guide',
    href: '/muslim-resource-guide',
    color: '#c47a3a',
    description: 'The practical expression of the Amanah — Khalifa, Islah, Salam in community action.',
    terms: ['Khalifa', 'Islah', 'Salam', 'Amanah', 'Haqq Al-Khalq', 'Masjid', 'Deen'],
  },
];

// ─── COLORS ───────────────────────────────────────────────────────────────────

const TYPE_COLORS: Record<string, string> = {
  key: '#D4A82A',
  ayat: '#2d7a47',
  name: '#8a8ab8',
  phase: '#2d8a87',
  lens: '#2d8a87',
  mrg: '#c47a3a',
  letter: 'rgba(179,138,42,0.7)',
};

const CAT_COLORS: Record<string, string> = {
  framework: '#D4A82A',
  irl: '#2d8a87',
  letter: 'rgba(179,138,42,0.7)',
  'name-concept': '#8a8ab8',
};

const CAT_LABELS: Record<string, string> = {
  framework: 'Framework Key',
  irl: 'IRL Lens',
  letter: 'Open Letter',
  'name-concept': 'Name Concept',
};

// ─── NAME CARD (full version for glossary) ────────────────────────────────────

function FullNameCard({ name, isExpanded, onToggle }: { name: NameEntry; isExpanded: boolean; onToggle: () => void }) {
  const [activeTab, setActiveTab] = useState<'overview' | 'sequences' | 'keys' | 'field'>('overview');

  return (
    <div
      className="transition-all duration-300 rounded-xl overflow-hidden"
      style={{
        border: isExpanded
          ? name.isAnchor ? '1px solid rgba(201,168,76,0.6)' : '1px solid rgba(179,138,42,0.4)'
          : '1px solid rgba(44,74,62,0.25)',
        background: isExpanded
          ? name.isAnchor ? 'rgba(201,168,76,0.06)' : 'rgba(28,43,37,0.5)'
          : 'rgba(28,43,37,0.25)',
        boxShadow: name.isAnchor && isExpanded ? '0 0 20px rgba(201,168,76,0.08)' : 'none',
      }}
    >
      {/* Card Header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-4 p-4 text-left hover:bg-white/3 transition-colors"
      >
        <span className="text-2xl flex-shrink-0 transition-colors" style={{ fontFamily: 'serif', minWidth: '2.5rem', textAlign: 'right', color: isExpanded ? 'rgba(201,168,76,0.8)' : 'rgba(201,168,76,0.3)' }}>
          {name.ar}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-xs font-black uppercase tracking-wide" style={{ color: isExpanded ? '#F5F0E8' : 'rgba(245,240,232,0.6)' }}>
              {name.transliteration}
            </span>
            <span className="font-mono text-xs px-1.5 py-0.5 rounded" style={{ background: 'rgba(201,168,76,0.1)', color: 'rgba(201,168,76,0.6)', border: '1px solid rgba(201,168,76,0.2)', fontSize: '0.55rem' }}>
              {name.frequency}×
            </span>
            <span className="font-mono text-xs px-1.5 py-0.5 rounded" style={{ background: 'rgba(44,74,62,0.3)', color: 'rgba(245,240,232,0.4)', border: '1px solid rgba(44,74,62,0.3)', fontSize: '0.55rem', textTransform: 'uppercase' }}>
              {name.category}
            </span>
            {name.isAnchor && (
              <span className="font-mono text-xs px-1.5 py-0.5 rounded" style={{ background: 'rgba(201,168,76,0.15)', color: '#c9a84c', border: '1px solid rgba(201,168,76,0.4)', fontSize: '0.55rem' }}>
                ⬡ Anchor
              </span>
            )}
            {name.letterSource && (
              <span className="font-mono text-xs px-1.5 py-0.5 rounded" style={{ background: 'rgba(201,168,76,0.08)', color: 'rgba(201,168,76,0.5)', border: '1px solid rgba(201,168,76,0.15)', fontSize: '0.5rem' }}>
                ✦ {name.letterSource.startsWith('hadith') ? '99 Names Hadith' : `Quran ${name.letterSource}`}
              </span>
            )}
          </div>
          <p className="font-mono text-xs mt-0.5" style={{ color: 'rgba(245,240,232,0.35)' }}>{name.meaning}</p>
          {name.pairedWith && (
            <p className="font-mono text-xs mt-0.5" style={{ color: 'rgba(45,138,135,0.6)', fontSize: '0.6rem' }}>
              ↔ Paired: {NAMES_DATA.find(n => n.id === name.pairedWith)?.transliteration || name.pairedWith}
            </p>
          )}
        </div>
        <svg className={`w-4 h-4 text-stone-500 flex-shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-stone-700/30">
          {/* Sub-tabs */}
          <div className="flex gap-1 px-4 pt-3 pb-0">
            {(['overview', 'sequences', 'keys', 'field'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className="font-mono text-xs px-3 py-1.5 rounded-t transition-colors capitalize"
                style={{
                  background: activeTab === tab ? 'rgba(201,168,76,0.15)' : 'transparent',
                  border: `1px solid ${activeTab === tab ? 'rgba(201,168,76,0.3)' : 'transparent'}`,
                  borderBottom: 'none',
                  color: activeTab === tab ? '#c9a84c' : 'rgba(245,240,232,0.35)',
                  fontSize: '0.6rem',
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                }}
              >
                {tab === 'overview' ? 'Root' : tab === 'sequences' ? `Sequences (${name.sequences.length})` : tab === 'keys' ? `Keys (${name.keyConnections.length})` : 'Field Prompt'}
              </button>
            ))}
          </div>

          <div className="px-4 pb-4 pt-3">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-3">
                <div className="p-3 rounded-lg" style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.12)' }}>
                  <p className="font-mono text-xs mb-1" style={{ color: 'rgba(201,168,76,0.5)', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Root: {name.root}</p>
                  <p className="font-mono text-xs" style={{ color: 'rgba(245,240,232,0.6)' }}>"{name.rootMeaning}"</p>
                </div>
                <div className="space-y-2">
                  {name.quranicRefs.map((ref, i) => (
                    <div key={i} className="p-3 rounded-lg" style={{ background: 'rgba(28,43,37,0.5)', border: '1px solid rgba(44,74,62,0.3)' }}>
                      <p className="text-right mb-1" style={{ fontFamily: 'serif', fontSize: 14, color: '#c9a84c', direction: 'rtl' }}>{ref.text.split('.')[0]}</p>
                      <p className="font-mono text-xs" style={{ color: 'rgba(201,168,76,0.5)', fontSize: '0.55rem' }}>Quran {ref.ref}</p>
                    </div>
                  ))}
                </div>
                {name.crossNameConnections.length > 0 && (
                  <div>
                    <p className="font-mono text-xs mb-2" style={{ color: 'rgba(245,240,232,0.3)', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Often appears with:</p>
                    <div className="flex flex-wrap gap-1.5">
                      {name.crossNameConnections.map((id) => {
                        const found = NAMES_DATA.find(n => n.id === id);
                        return found ? (
                          <span key={id} className="font-mono text-xs px-2 py-0.5 rounded" style={{ background: 'rgba(44,74,62,0.3)', color: 'rgba(245,240,232,0.5)', border: '1px solid rgba(44,74,62,0.3)', fontSize: '0.6rem' }}>
                            {found.transliteration}
                          </span>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Sequences Tab */}
            {activeTab === 'sequences' && (
              <div className="space-y-2">
                {name.sequences.length === 0 ? (
                  <p className="text-xs text-stone-600 py-4 text-center">No sequences documented yet.</p>
                ) : (
                  name.sequences.map((seq, i) => (
                    <div key={i} className="p-3 rounded-lg" style={{ background: 'rgba(28,43,37,0.4)', border: '1px solid rgba(44,74,62,0.25)' }}>
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-mono text-xs font-bold" style={{ color: '#F5F0E8', fontSize: '0.7rem' }}>S{String(seq.seqNum).padStart(2, '0')} — {seq.seqTitle}</span>
                        <span className="font-mono text-xs px-1.5 py-0.5 rounded" style={{ background: 'rgba(45,138,135,0.15)', color: '#2d8a87', border: '1px solid rgba(45,138,135,0.25)', fontSize: '0.5rem', textTransform: 'uppercase' }}>
                          {seq.layer}
                        </span>
                      </div>
                      <p className="font-mono text-xs" style={{ color: 'rgba(245,240,232,0.4)', fontSize: '0.65rem' }}>{seq.city} · {seq.biome}</p>
                      <p className="text-xs mt-1" style={{ color: 'rgba(212,197,169,0.6)', lineHeight: 1.6, fontSize: '0.7rem' }}>{seq.role}</p>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Keys Tab */}
            {activeTab === 'keys' && (
              <div className="space-y-2">
                {name.keyConnections.length === 0 ? (
                  <p className="text-xs text-stone-600 py-4 text-center">No key connections documented yet.</p>
                ) : (
                  name.keyConnections.map((kc, i) => (
                    <div key={i} className="p-3 rounded-lg" style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.15)' }}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-xs px-1.5 py-0.5 rounded" style={{ background: 'rgba(201,168,76,0.15)', color: '#c9a84c', border: '1px solid rgba(201,168,76,0.3)', fontSize: '0.55rem' }}>
                          🔑 {kc.keyId} ({kc.keyNum})
                        </span>
                      </div>
                      <p className="text-xs" style={{ color: 'rgba(212,197,169,0.65)', lineHeight: 1.6, fontSize: '0.7rem' }}>{kc.connection}</p>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Field Prompt Tab */}
            {activeTab === 'field' && (
              <div className="p-4 rounded-lg" style={{ background: 'rgba(44,74,62,0.2)', border: '1px solid rgba(44,74,62,0.35)', borderLeft: '3px solid rgba(126,200,160,0.5)' }}>
                <p className="font-mono text-xs mb-2" style={{ color: 'rgba(126,200,160,0.6)', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  🌱 Caretaker Field Prompt
                </p>
                <p className="text-sm" style={{ color: 'rgba(212,197,169,0.75)', lineHeight: 1.8, fontStyle: 'italic' }}>
                  {name.fieldPrompt}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── SUNNAT TIMELINE VIEWER ───────────────────────────────────────────────────

function SunnatTimelineViewer({ timeline }: { timeline: SunnatTimeline }) {
  const [activeStep, setActiveStep] = useState(0);
  const step = timeline.steps[activeStep];

  return (
    <div className="rounded-xl overflow-hidden" style={{ background: 'rgba(6,13,18,0.8)', border: '1px solid rgba(44,74,62,0.3)' }}>
      <div className="px-5 py-4 border-b border-stone-800/40">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="font-mono text-xs mb-1" style={{ color: 'rgba(45,138,135,0.6)', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              {timeline.city} · {timeline.biome}
            </p>
            <h4 className="font-mono text-sm font-black" style={{ color: '#F5F0E8' }}>{timeline.seqTitle}</h4>
          </div>
        </div>
        <p className="text-xs mt-2 italic" style={{ color: 'rgba(201,168,76,0.5)', fontFamily: 'serif', direction: 'rtl', textAlign: 'right' }}>
          {timeline.ayah}
        </p>
      </div>

      {/* Step selector */}
      <div className="flex gap-1 px-4 py-3 overflow-x-auto">
        {timeline.steps.map((s, i) => (
          <button
            key={i}
            onClick={() => setActiveStep(i)}
            className="flex-shrink-0 font-mono text-xs px-3 py-1.5 rounded transition-colors"
            style={{
              background: activeStep === i ? 'rgba(45,138,135,0.2)' : 'rgba(28,43,37,0.4)',
              border: `1px solid ${activeStep === i ? 'rgba(45,138,135,0.4)' : 'rgba(44,74,62,0.25)'}`,
              color: activeStep === i ? '#2d8a87' : 'rgba(245,240,232,0.35)',
              fontSize: '0.6rem',
            }}
          >
            Step {s.step}
          </button>
        ))}
      </div>

      {/* Active step */}
      {step && (
        <div className="px-5 pb-5">
          <div className="p-4 rounded-lg" style={{ background: 'rgba(45,138,135,0.06)', border: '1px solid rgba(45,138,135,0.2)' }}>
            <p className="font-mono text-xs mb-2" style={{ color: '#2d8a87', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Step {step.step}
            </p>
            <p className="text-sm mb-3" style={{ color: 'rgba(212,197,169,0.8)', lineHeight: 1.7 }}>{step.description}</p>
            {step.amr && (
              <p className="font-mono text-xs mb-2" style={{ color: 'rgba(201,168,76,0.6)', fontFamily: 'serif', fontSize: 16 }}>
                {step.amr}
              </p>
            )}
            {step.note && (
              <p className="text-xs italic" style={{ color: 'rgba(212,197,169,0.45)', lineHeight: 1.6 }}>{step.note}</p>
            )}
            <div className="flex flex-wrap gap-1.5 mt-3">
              {step.namesActive.map((n) => (
                <span key={n} className="font-mono text-xs px-2 py-0.5 rounded" style={{ background: 'rgba(201,168,76,0.1)', color: '#c9a84c', border: '1px solid rgba(201,168,76,0.2)', fontSize: '0.6rem' }}>
                  {n}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────

type TabType = 'glossary' | 'names' | 'sequences' | 'keys' | 'pages';

export default function SLGlossaryPage() {
  const [activeTab, setActiveTab] = useState<TabType>('glossary');
  const [activeId, setActiveId] = useState<string>('taqwah');
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [expandedNameId, setExpandedNameId] = useState<string | null>(null);
  const [nameCategoryFilter, setNameCategoryFilter] = useState('All');
  const [activeTimelineId, setActiveTimelineId] = useState<string>('leaf-sequence');
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const filtered = useMemo(() => {
    return GLOSSARY_TERMS.filter(t => {
      const matchesSearch = search === '' ||
        t.term.toLowerCase().includes(search.toLowerCase()) ||
        t.arabic.includes(search) ||
        t.definition.toLowerCase().includes(search.toLowerCase()) ||
        t.root.includes(search) ||
        t.relatedTerms.some(r => r.toLowerCase().includes(search.toLowerCase()));
      const matchesCat = categoryFilter === 'all' || t.category === categoryFilter;
      return matchesSearch && matchesCat;
    });
  }, [search, categoryFilter]);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (search.length >= 3) {
      searchDebounceRef.current = setTimeout(() => {
        trackGlossarySearch(search, filtered.length);
      }, 800);
    }
    return () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current); };
  }, [search, filtered.length]);

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    trackResonance('glossary_tab', tab, tab, 'view');
  };

  const handleTermExpand = (termId: string, termName: string) => {
    setActiveId(termId);
    trackResonance('glossary_term', termId, termName, 'term_expand');
  };

  const filteredNames = useMemo(() => {
    return NAMES_DATA.filter(n => {
      const matchesSearch = search === '' ||
        n.transliteration.toLowerCase().includes(search.toLowerCase()) ||
        n.ar.includes(search) ||
        n.meaning.toLowerCase().includes(search.toLowerCase());
      const matchesCat = nameCategoryFilter === 'All' || n.category === nameCategoryFilter;
      return matchesSearch && matchesCat;
    });
  }, [search, nameCategoryFilter]);

  const filteredPages = useMemo(() => {
    if (search === '') return PAGE_INDEX;
    return PAGE_INDEX.filter(p =>
      p.page.toLowerCase().includes(search.toLowerCase()) ||
      p.terms.some(t => t.toLowerCase().includes(search.toLowerCase()))
    );
  }, [search]);

  const activeTerm = GLOSSARY_TERMS.find(t => t.id === activeId) ?? GLOSSARY_TERMS[0];
  const activeTimeline = SUNNAT_TIMELINES.find(t => t.seqId === activeTimelineId) ?? SUNNAT_TIMELINES[0];

  const tabs: { id: TabType; label: string; count: number | string }[] = [
    { id: 'glossary', label: 'SL Terms', count: GLOSSARY_TERMS.length },
    { id: 'names', label: 'Names of Allah', count: NAMES_DATA.length },
    { id: 'sequences', label: 'Sunnat Sequences', count: SUNNAT_TIMELINES.length },
    { id: 'keys', label: 'Keys Index', count: 7 },
    { id: 'pages', label: 'Page Index', count: PAGE_INDEX.length },
  ];

  return (
    <div className="min-h-screen deep-green-bg">
      <Header />
      <main>
        {/* ── Hero ─────────────────────────────────────────────────────────── */}
        <section className="relative pt-32 pb-16 px-6 overflow-hidden" style={{ background: 'linear-gradient(to bottom, #0d1a14, #1C2B25)' }}>
          <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(139,105,20,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(139,105,20,0.04) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
          <div className="max-w-4xl mx-auto text-center relative z-10">
            <p className="font-mono text-xs tracking-widest uppercase mb-4" style={{ color: 'rgba(139,105,20,0.7)' }}>
              ✦ Sacred Language Glossary & Names Reference · معجم اللغة المقدسة
            </p>
            <h1 className="font-mono text-5xl md:text-6xl font-black uppercase tracking-tight leading-none mb-4" style={{ color: '#F5F0E8' }}>
              SL GLOSSARY<br />
              <span style={{ color: '#8a8ab8', fontSize: '0.6em' }}>+ NAMES REFERENCE</span>
            </h1>
            <p className="font-mono text-sm max-w-xl mx-auto leading-relaxed mb-6" style={{ color: 'rgba(245,240,232,0.5)' }}>
              The unified vocabulary of the framework — SL terms, Names of Allah with root analyses, Sunnat sequences, Keys index, and page cross-references. Every term is a door.
            </p>
            {/* Origin verse */}
            <div className="max-w-lg mx-auto mb-6 p-4 rounded-xl" style={{ background: 'rgba(6,13,18,0.6)', border: '1px solid rgba(201,168,76,0.2)' }}>
              <p style={{ fontFamily: "'Amiri', serif", fontSize: 18, color: '#D4A82A', direction: 'rtl', marginBottom: 4 }}>
                هُوَ اللَّهُ الْخَالِقُ الْبَارِئُ الْمُصَوِّرُ ۖ لَهُ الْأَسْمَاءُ الْحُسْنَىٰ
              </p>
              <p className="font-mono text-xs" style={{ color: 'rgba(201,168,76,0.5)', fontSize: '0.6rem' }}>
                "He is Allah — Al-Khaliq, Al-Bari, Al-Musawwir. To Him belong the most beautiful Names." — Quran 59:24
              </p>
            </div>
            {/* Search */}
            <div className="max-w-md mx-auto relative">
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search terms, Arabic, Names, definitions…"
                className="w-full px-4 py-3 font-mono text-sm focus:outline-none"
                style={{ background: 'rgba(45,138,135,0.06)', border: '1px solid rgba(45,138,135,0.3)', color: '#F5F0E8' }}
              />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 font-mono text-xs" style={{ color: 'rgba(179,138,42,0.4)' }}>✕</button>
              )}
            </div>
            {search && (
              <p className="font-mono text-xs mt-3 tracking-widest" style={{ color: 'rgba(179,138,42,0.5)' }}>
                {filtered.length} terms · {filteredNames.length} names · {filteredPages.length} pages matching "{search}"
              </p>
            )}
          </div>
        </section>

        {/* ── Tabs ─────────────────────────────────────────────────────────── */}
        <div className="sticky top-0 z-30 px-6 py-3 flex gap-2 overflow-x-auto" style={{ background: 'rgba(13,26,20,0.97)', borderBottom: '1px solid rgba(139,105,20,0.15)' }}>
          <div className="max-w-6xl mx-auto w-full flex gap-2">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className="flex items-center gap-2 px-4 py-2 font-mono text-xs tracking-widest uppercase transition-all flex-shrink-0"
                style={{
                  background: activeTab === tab.id ? 'rgba(139,105,20,0.2)' : 'rgba(28,43,37,0.4)',
                  border: `1px solid ${activeTab === tab.id ? 'rgba(179,138,42,0.5)' : 'rgba(44,74,62,0.3)'}`,
                  color: activeTab === tab.id ? '#F5F0E8' : 'rgba(245,240,232,0.4)',
                  borderBottom: activeTab === tab.id ? '2px solid #D4A82A' : '1px solid rgba(44,74,62,0.3)',
                  fontSize: '0.65rem',
                }}
              >
                {tab.id === 'names' ? '✦ ' : tab.id === 'sequences' ? '◎ ' : ''}{tab.label}
                <span className="font-mono px-1.5 py-0.5 rounded" style={{ background: 'rgba(212,168,42,0.04)', color: 'rgba(212,168,42,0.5)', border: '1px solid rgba(212,168,42,0.15)', fontSize: '0.55rem' }}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* ── TAB: GLOSSARY ─────────────────────────────────────────────────── */}
        {activeTab === 'glossary' && (
          <section className="py-8 px-6" style={{ background: '#0d1a14' }}>
            <div className="max-w-6xl mx-auto">
              <div className="flex gap-2 mb-6 flex-wrap">
                {(['all', 'framework', 'irl', 'letter', 'name-concept'] as const).map(cat => (
                  <button
                    key={cat}
                    onClick={() => setCategoryFilter(cat)}
                    className="flex items-center gap-2 px-4 py-2 font-mono uppercase transition-all flex-shrink-0"
                    style={{
                      background: categoryFilter === cat ? (cat === 'all' ? 'rgba(139,105,20,0.2)' : `${CAT_COLORS[cat]}18`) : 'rgba(28,43,37,0.4)',
                      border: `1px solid ${categoryFilter === cat ? (cat === 'all' ? 'rgba(179,138,42,0.4)' : `${CAT_COLORS[cat]}40`) : 'rgba(44,74,62,0.3)'}`,
                      color: categoryFilter === cat ? (cat === 'all' ? '#D4A82A' : CAT_COLORS[cat]) : 'rgba(245,240,232,0.35)',
                      fontSize: '0.6rem',
                      letterSpacing: '0.08em',
                    }}
                  >
                    {cat === 'all' ? `All (${GLOSSARY_TERMS.length})` : `${CAT_LABELS[cat]} (${GLOSSARY_TERMS.filter(t => t.category === cat).length})`}
                  </button>
                ))}
              </div>

              <div className="flex flex-col lg:flex-row gap-8">
                {/* Left: Term index */}
                <div className="lg:w-72 flex-shrink-0">
                  <div className="sticky top-24">
                    <p className="font-mono mb-3" style={{ color: 'rgba(139,105,20,0.5)', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                      {filtered.length} Terms
                    </p>
                    <div className="flex flex-col gap-1 max-h-[70vh] overflow-y-auto pr-1">
                      {filtered.map(term => (
                        <button
                          key={term.id}
                          onClick={() => handleTermExpand(term.id, term.term)}
                          className="flex items-center gap-3 p-3 text-left transition-all duration-200"
                          style={{
                            background: activeId === term.id ? 'rgba(139,105,20,0.12)' : 'rgba(28,43,37,0.4)',
                            border: `1px solid ${activeId === term.id ? 'rgba(179,138,42,0.4)' : 'rgba(44,74,62,0.3)'}`,
                            color: activeId === term.id ? '#F5F0E8' : 'rgba(245,240,232,0.4)',
                            borderBottom: activeId === term.id ? '2px solid #D4A82A' : '1px solid rgba(44,74,62,0.3)',
                          }}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-mono font-black" style={{ color: '#F5F0E8', fontSize: '0.7rem' }}>{term.term}</span>
                              <span style={{ fontFamily: "'Amiri', serif", fontSize: 12, color: '#D4A82A', direction: 'rtl' }}>{term.arabic}</span>
                            </div>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="font-mono" style={{ color: 'rgba(245,240,232,0.2)', fontSize: '0.55rem' }}>{term.root}</span>
                              {term.keyTag && (
                                <span className="font-mono px-1" style={{ background: 'rgba(212,168,42,0.08)', color: 'rgba(212,168,42,0.4)', border: '1px solid rgba(212,168,42,0.12)', fontSize: '0.5rem' }}>{term.keyTag}</span>
                              )}
                            </div>
                          </div>
                        </button>
                      ))}
                      {filtered.length === 0 && (
                        <p className="font-mono p-4 text-center" style={{ color: 'rgba(245,240,232,0.3)', fontSize: '0.65rem' }}>No terms match "{search}"</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Right: Term detail */}
                {activeTerm && (
                  <div className="flex-1">
                    <div className="p-8 rounded-xl" style={{ background: 'rgba(6,13,18,0.7)', border: '1px solid rgba(179,138,42,0.15)', borderTop: `3px solid ${CAT_COLORS[activeTerm.category]}` }}>
                      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
                        <div>
                          <div className="flex items-center gap-4 mb-2 flex-wrap">
                            <h2 className="font-mono text-3xl font-black uppercase tracking-tight" style={{ color: '#F5F0E8' }}>{activeTerm.term}</h2>
                            <span style={{ fontFamily: "'Amiri', serif", fontSize: 28, color: '#D4A82A', direction: 'rtl' }}>{activeTerm.arabic}</span>
                          </div>
                          <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                            <span className="font-mono" style={{ color: 'rgba(245,240,232,0.35)', fontSize: '0.65rem' }}>"{activeTerm.rootMeaning}"</span>
                            <span className="font-mono px-2 py-1" style={{ background: 'rgba(139,105,20,0.15)', color: 'rgba(179,105,20,0.8)', border: '1px solid rgba(139,105,20,0.3)', fontSize: '0.65rem' }}>Root: {activeTerm.root}</span>
                            <span className="font-mono" style={{ color: 'rgba(245,240,232,0.25)', fontSize: '0.65rem' }}>/{activeTerm.pronunciation}/</span>
                            <span className="font-mono px-1" style={{ background: `${CAT_COLORS[activeTerm.category]}15`, color: CAT_COLORS[activeTerm.category], border: `1px solid ${CAT_COLORS[activeTerm.category]}30`, fontSize: '0.6rem' }}>{CAT_LABELS[activeTerm.category]}</span>
                            {activeTerm.keyTag && (
                              <span className="font-mono px-1" style={{ background: 'rgba(212,168,42,0.1)', color: '#D4A82A', border: '1px solid rgba(212,168,42,0.25)', fontSize: '0.6rem' }}>{activeTerm.keyTag}</span>
                            )}
                          </div>
                        </div>
                        <BookmarkButton
                          item={{ item_type: 'glossary_term', item_id: activeTerm.id, item_title: activeTerm.term, item_arabic: activeTerm.arabic, item_href: `/sl-glossary#${activeTerm.id}`, item_description: activeTerm.definition.slice(0, 120), item_category: activeTerm.category }}
                          size="md"
                          showLabel
                        />
                      </div>

                      <div className="mb-6 p-5 rounded-lg" style={{ background: 'rgba(139,105,20,0.06)', border: '1px solid rgba(139,105,20,0.15)' }}>
                        <p className="font-mono mb-2" style={{ color: 'rgba(139,105,20,0.3)', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Definition</p>
                        <p style={{ color: 'rgba(212,197,169,0.65)', fontSize: 14, lineHeight: 1.8 }}>{activeTerm.definition}</p>
                      </div>

                      <div className="mb-6">
                        <p className="font-mono mb-3" style={{ color: 'rgba(139,105,20,0.5)', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Etymology & Root Analysis</p>
                        <p style={{ color: 'rgba(212,197,169,0.65)', fontSize: 13, lineHeight: 1.8 }}>{activeTerm.etymology}</p>
                      </div>

                      <div className="mb-6">
                        <p className="font-mono mb-3" style={{ color: 'rgba(139,105,20,0.5)', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Quranic Roots</p>
                        <div className="flex flex-col gap-3">
                          {activeTerm.quranicRoots.map((qr, i) => (
                            <div key={i} className="p-4 rounded-lg" style={{ background: 'rgba(28,43,37,0.5)', border: '1px solid rgba(44,74,62,0.3)' }}>
                              <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div className="flex-1">
                                  <p style={{ fontFamily: "'Amiri', serif", fontSize: 18, color: '#D4A82A', direction: 'rtl', textAlign: 'right', marginBottom: 4 }}>{qr.arabic}</p>
                                  <p style={{ color: 'rgba(212,197,169,0.65)', fontSize: 12, fontStyle: 'italic' }}>"{qr.translation}"</p>
                                </div>
                                <a href={qr.href} target="_blank" rel="noopener noreferrer" className="font-mono flex-shrink-0" style={{ color: 'rgba(179,138,42,0.6)', fontSize: '0.65rem' }}>{qr.ref} ↗</a>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="mb-6">
                        <p className="font-mono mb-3" style={{ color: 'rgba(139,105,20,0.5)', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Cross-Links</p>
                        <div className="flex flex-wrap gap-2">
                          {activeTerm.crossLinks.map((cl, i) => (
                            <Link key={i} href={cl.href} className="font-mono uppercase px-3 py-1.5 transition-colors" style={{ background: `${TYPE_COLORS[cl.type]}12`, border: `1px solid ${TYPE_COLORS[cl.type]}30`, color: TYPE_COLORS[cl.type], fontSize: '0.6rem', letterSpacing: '0.06em', textDecoration: 'none' }}
                              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = `${TYPE_COLORS[cl.type]}25`; }}
                              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = `${TYPE_COLORS[cl.type]}12`; }}
                            >
                              {cl.type === 'key' ? '🔑' : cl.type === 'name' ? '✦' : cl.type === 'ayat' ? '🌿' : cl.type === 'lens' ? '◎' : cl.type === 'mrg' ? '🕌' : cl.type === 'letter' ? '📜' : '◎'} {cl.label}
                            </Link>
                          ))}
                        </div>
                      </div>

                      <div className="mb-6">
                        <p className="font-mono mb-3" style={{ color: 'rgba(139,105,20,0.5)', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Usage Examples</p>
                        <div className="flex flex-col gap-3">
                          {activeTerm.usageExamples.map((ex, i) => (
                            <div key={i} className="flex items-start gap-3 p-3 rounded-lg" style={{ background: 'rgba(28,43,37,0.4)', border: '1px solid rgba(44,74,62,0.2)' }}>
                              <span className="font-mono flex-shrink-0 mt-0.5" style={{ color: 'rgba(179,138,42,0.4)', fontSize: '0.6rem' }}>{String(i + 1).padStart(2, '0')}</span>
                              <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 12, lineHeight: 1.7 }}>{ex}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <p className="font-mono mb-3" style={{ color: 'rgba(139,105,20,0.5)', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Related Terms</p>
                        <div className="flex flex-wrap gap-2">
                          {activeTerm.relatedTerms.map((rt, i) => {
                            const found = GLOSSARY_TERMS.find(t => t.term === rt || t.id === rt.toLowerCase().replace(/\s+/g, '-'));
                            return (
                              <button key={i} onClick={() => found && setActiveId(found.id)} className="font-mono uppercase px-3 py-1.5 transition-colors"
                                style={{ background: found ? 'rgba(44,74,62,0.3)' : 'rgba(28,43,37,0.3)', border: `1px solid ${found ? 'rgba(44,74,62,0.4)' : 'rgba(44,74,62,0.2)'}`, color: found ? 'rgba(245,240,232,0.5)' : 'rgba(245,240,232,0.25)', cursor: found ? 'pointer' : 'default', fontSize: '0.6rem', letterSpacing: '0.06em' }}
                                onMouseEnter={(e) => { if (found) { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(179,138,42,0.4)'; (e.currentTarget as HTMLElement).style.color = 'rgba(245,240,232,0.8)'; } }}
                                onMouseLeave={(e) => { if (found) { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(44,74,62,0.4)'; (e.currentTarget as HTMLElement).style.color = 'rgba(245,240,232,0.5)'; } }}
                              >
                                {rt}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* ── TAB: NAMES OF ALLAH ───────────────────────────────────────────── */}
        {activeTab === 'names' && (
          <section className="py-8 px-6" style={{ background: '#0d1a14' }}>
            <div className="max-w-6xl mx-auto">
              {/* Header */}
              <div className="mb-8 p-6 rounded-xl" style={{ background: 'rgba(6,13,18,0.7)', border: '1px solid rgba(179,138,42,0.2)', borderLeft: '3px solid #D4A82A' }}>
                <p className="font-mono mb-2" style={{ color: 'rgba(139,105,20,0.5)', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Origin Verse — Quran 59:23-24</p>
                <p style={{ fontFamily: "'Amiri', serif", fontSize: 20, color: '#D4A82A', direction: 'rtl', textAlign: 'right', marginBottom: 8 }}>
                  هُوَ اللَّهُ الَّذِي لَا إِلَٰهَ إِلَّا هُوَ الْمَلِكُ الْقُدُّوسُ السَّلَامُ الْمُؤْمِنُ الْمُهَيْمِنُ الْعَزِيزُ الْجَبَّارُ الْمُتَكَبِّرُ
                </p>
                <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 12, fontStyle: 'italic', marginBottom: 4 }}>
                  "He is Allah, other than whom there is no deity — the Sovereign, the Holy, the Source of Peace, the Granter of Security, the Overseer, the Exalted in Might, the Compeller, the Supremely Great."
                </p>
                <p className="font-mono" style={{ color: 'rgba(179,138,42,0.4)', fontSize: '0.6rem' }}>
                  The Names of Allah are not merely attributes — they are the doors through which the caretaker reads creation. Each Name is a lens, a field prompt, a recognition arc.
                </p>
              </div>

              {/* Category filter */}
              <div className="flex flex-wrap gap-2 mb-6">
                {NAME_CATEGORIES.map(cat => (
                  <button key={cat} onClick={() => setNameCategoryFilter(cat)}
                    className="font-mono uppercase px-3 py-1.5 transition-colors"
                    style={{
                      background: nameCategoryFilter === cat ? 'rgba(201,168,76,0.15)' : 'rgba(28,43,37,0.4)',
                      border: `1px solid ${nameCategoryFilter === cat ? 'rgba(201,168,76,0.4)' : 'rgba(44,74,62,0.3)'}`,
                      color: nameCategoryFilter === cat ? '#c9a84c' : 'rgba(245,240,232,0.35)',
                      fontSize: '0.6rem', letterSpacing: '0.06em',
                    }}
                  >
                    {cat} {cat !== 'All' ? `(${NAMES_DATA.filter(n => n.category === cat).length})` : `(${NAMES_DATA.length})`}
                  </button>
                ))}
              </div>

              {/* Names grid */}
              <div className="space-y-2">
                {filteredNames.map(name => (
                  <FullNameCard
                    key={name.id}
                    name={name}
                    isExpanded={expandedNameId === name.id}
                    onToggle={() => setExpandedNameId(expandedNameId === name.id ? null : name.id)}
                  />
                ))}
                {filteredNames.length === 0 && (
                  <div className="text-center py-12" style={{ color: 'rgba(245,240,232,0.3)' }}>
                    <p className="font-mono text-sm">No Names match your search.</p>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* ── TAB: SUNNAT SEQUENCES ─────────────────────────────────────────── */}
        {activeTab === 'sequences' && (
          <section className="py-8 px-6" style={{ background: '#0d1a14' }}>
            <div className="max-w-6xl mx-auto">
              <div className="mb-6">
                <p className="font-mono mb-1" style={{ color: 'rgba(139,105,20,0.5)', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Sunnat Allah — Sequence Timelines</p>
                <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 12 }}>
                  Step-by-step timelines of creation sequences — each step names the active Names of Allah and the divine pattern (Sunnat) being expressed.
                </p>
              </div>

              <div className="flex flex-col lg:flex-row gap-6">
                {/* Sequence selector */}
                <div className="lg:w-56 flex-shrink-0">
                  <div className="sticky top-24 space-y-1">
                    {SUNNAT_TIMELINES.map(tl => (
                      <button key={tl.seqId} onClick={() => setActiveTimelineId(tl.seqId)}
                        className="w-full text-left p-3 rounded-lg transition-colors"
                        style={{
                          background: activeTimelineId === tl.seqId ? 'rgba(45,138,135,0.15)' : 'rgba(28,43,37,0.3)',
                          border: `1px solid ${activeTimelineId === tl.seqId ? 'rgba(45,138,135,0.4)' : 'rgba(44,74,62,0.25)'}`,
                        }}
                      >
                        <p className="font-mono font-bold" style={{ color: activeTimelineId === tl.seqId ? '#2d8a87' : 'rgba(245,240,232,0.5)', fontSize: '0.7rem' }}>{tl.seqTitle}</p>
                        <p className="font-mono mt-0.5" style={{ color: 'rgba(245,240,232,0.3)', fontSize: '0.55rem' }}>{tl.city}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Active timeline */}
                <div className="flex-1">
                  {activeTimeline && <SunnatTimelineViewer timeline={activeTimeline} />}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* ── TAB: KEYS INDEX ───────────────────────────────────────────────── */}
        {activeTab === 'keys' && (
          <section className="py-8 px-6" style={{ background: '#0d1a14' }}>
            <div className="max-w-6xl mx-auto">
              <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
                <div>
                  <p className="font-mono mb-1" style={{ color: 'rgba(139,105,20,0.5)', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Keys to Kulli Haal — Term Index</p>
                  <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 12 }}>All SL terms organized by their Key — with inline cross-links to the full glossary.</p>
                </div>
                <Link href="/keys-to-kulli-haal" className="font-mono uppercase px-4 py-2 transition-all" style={{ background: 'rgba(212,168,42,0.1)', border: '1px solid rgba(212,168,42,0.3)', color: '#D4A82A', fontSize: '0.65rem', letterSpacing: '0.08em', textDecoration: 'none' }}>
                  🔑 Full Keys Page →
                </Link>
              </div>

              {/* IRL Lens terms */}
              <div className="mb-8 p-6 rounded-xl" style={{ background: 'rgba(6,13,18,0.7)', border: '1px solid rgba(45,138,135,0.3)', borderTop: '3px solid #2d8a87' }}>
                <div className="flex items-center gap-3 mb-4">
                  <span className="font-mono uppercase px-2 py-1" style={{ background: 'rgba(45,138,135,0.15)', color: '#2d8a87', border: '1px solid rgba(45,138,135,0.3)', fontSize: '0.6rem', letterSpacing: '0.08em' }}>IRL Lens</span>
                  <h3 className="font-mono font-black uppercase" style={{ color: '#F5F0E8', fontSize: '0.85rem' }}>Inner Reality Lens — 8-Layer Vocabulary</h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {GLOSSARY_TERMS.filter(t => t.category === 'irl').map(term => (
                    <button key={term.id} onClick={() => { setActiveId(term.id); setActiveTab('glossary'); }}
                      className="p-3 text-left rounded-lg transition-all"
                      style={{ background: 'rgba(45,138,135,0.06)', border: '1px solid rgba(45,138,135,0.2)' }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(45,138,135,0.5)'; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(45,138,135,0.2)'; }}
                    >
                      <div className="font-mono font-black mb-1" style={{ color: '#F5F0E8', fontSize: '0.7rem' }}>{term.term}</div>
                      <div style={{ fontFamily: "'Amiri', serif", fontSize: 12, color: '#D4A82A', direction: 'rtl' }}>{term.arabic}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Keys groups */}
              {['Key 01', 'Key 02', 'Key 03', 'Key 04', 'Key 05', 'Key 06', 'Key 07', 'Other'].map(keyLabel => {
                const terms = GLOSSARY_TERMS.filter(t => (t.keyTag ?? 'Other') === keyLabel);
                if (terms.length === 0) return null;
                const keyNames: Record<string, string> = {
                  'Key 01': 'AYAT — Recognition of Signs',
                  'Key 02': 'HAAL — State of Being',
                  'Key 03': 'TAQWAH — God-Consciousness as Adornment',
                  'Key 04': 'MIZAN — The Cosmic Balance',
                  'Key 05': 'AMANAH — The Sacred Trust',
                  'Key 06': 'FALAH — Cultivation & Flourishing',
                  'Key 07': 'YAQEEN — Certainty',
                  'Other': 'Framework Concepts',
                };
                return (
                  <div key={keyLabel} className="mb-6 p-6 rounded-xl" style={{ background: 'rgba(6,13,18,0.7)', border: '1px solid rgba(179,138,42,0.15)', borderTop: '3px solid rgba(212,168,42,0.4)' }}>
                    <div className="flex items-center gap-3 mb-4 flex-wrap">
                      <span className="font-mono uppercase px-2 py-1" style={{ background: 'rgba(212,168,42,0.12)', color: '#D4A82A', border: '1px solid rgba(212,168,42,0.25)', fontSize: '0.6rem', letterSpacing: '0.08em' }}>{keyLabel}</span>
                      <h3 className="font-mono font-black uppercase" style={{ color: '#F5F0E8', fontSize: '0.85rem' }}>{keyNames[keyLabel]}</h3>
                      <Link href="/keys-to-kulli-haal" className="font-mono ml-auto" style={{ color: 'rgba(212,168,42,0.4)', fontSize: '0.6rem', textDecoration: 'none' }}>Open Key →</Link>
                    </div>
                    <div className="flex flex-col gap-3">
                      {terms.map(term => (
                        <button key={term.id} onClick={() => { setActiveId(term.id); setActiveTab('glossary'); }}
                          className="p-4 text-left rounded-lg transition-all"
                          style={{ background: 'rgba(28,43,37,0.4)', border: '1px solid rgba(44,74,62,0.3)' }}
                          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(212,168,42,0.3)'; }}
                          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(44,74,62,0.3)'; }}
                        >
                          <div className="flex items-center gap-3 mb-2">
                            <span className="font-mono font-black" style={{ color: '#F5F0E8', fontSize: '0.8rem' }}>{term.term}</span>
                            <span style={{ fontFamily: "'Amiri', serif", fontSize: 14, color: '#D4A82A', direction: 'rtl' }}>{term.arabic}</span>
                          </div>
                          <p className="font-mono mb-1" style={{ color: 'rgba(179,138,42,0.5)', fontSize: '0.6rem' }}>Root: {term.root} — "{term.rootMeaning}"</p>
                          <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 11, lineHeight: 1.6 }}>{term.definition.slice(0, 90)}...</p>
                          <p className="font-mono mt-2" style={{ color: 'rgba(45,138,135,0.5)', fontSize: '0.6rem' }}>→ Click to view full entry</p>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ── TAB: PAGE INDEX ───────────────────────────────────────────────── */}
        {activeTab === 'pages' && (
          <section className="py-8 px-6" style={{ background: '#0d1a14' }}>
            <div className="max-w-6xl mx-auto">
              <div className="mb-6">
                <p className="font-mono mb-1" style={{ color: 'rgba(139,105,20,0.5)', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Page Index — Where Each Term Lives</p>
                <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 12 }}>Every page in the Haal Project and the SL terms that live there — with inline links to the glossary.</p>
              </div>

              <div className="flex flex-col gap-6">
                {filteredPages.map(page => (
                  <div key={page.page} className="p-6 rounded-xl" style={{ background: 'rgba(6,13,18,0.7)', border: `1px solid ${page.color}25`, borderLeft: `3px solid ${page.color}` }}>
                    <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
                      <div>
                        <h3 className="font-mono font-black uppercase mb-1" style={{ color: '#F5F0E8', fontSize: '0.9rem' }}>{page.page}</h3>
                        <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 12 }}>{page.description}</p>
                      </div>
                      <Link href={page.href} className="font-mono uppercase px-4 py-2 flex-shrink-0 transition-all"
                        style={{ background: `${page.color}12`, border: `1px solid ${page.color}30`, color: page.color, fontSize: '0.6rem', letterSpacing: '0.08em', textDecoration: 'none' }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = `${page.color}22`; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = `${page.color}12`; }}
                      >
                        Visit Page →
                      </Link>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {page.terms.map((termLabel, i) => {
                        const baseTerm = termLabel.split(' (')[0];
                        const found = GLOSSARY_TERMS.find(t =>
                          t.term === baseTerm ||
                          t.term.toLowerCase() === baseTerm.toLowerCase() ||
                          t.id === baseTerm.toLowerCase().replace(/[\s/]+/g, '-')
                        );
                        return (
                          <button key={i} onClick={() => { if (found) { setActiveId(found.id); setActiveTab('glossary'); } }}
                            className="font-mono transition-all"
                            style={{ background: found ? `${page.color}10` : 'rgba(28,43,37,0.3)', border: `1px solid ${found ? page.color + '30' : 'rgba(44,74,62,0.2)'}`, color: found ? page.color : 'rgba(245,240,232,0.3)', cursor: found ? 'pointer' : 'default', fontSize: '0.6rem', letterSpacing: '0.06em', padding: '4px 10px' }}
                            onMouseEnter={(e) => { if (found) { (e.currentTarget as HTMLElement).style.background = `${page.color}22`; } }}
                            onMouseLeave={(e) => { if (found) { (e.currentTarget as HTMLElement).style.background = `${page.color}10`; } }}
                          >
                            {termLabel}{found && <span className="ml-1 opacity-50">→</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* All terms quick reference (glossary tab only) */}
        {activeTab === 'glossary' && (
          <section className="py-16 px-6" style={{ background: '#060d12', borderTop: '1px solid rgba(139,105,20,0.1)' }}>
            <div className="max-w-6xl mx-auto">
              <p className="font-mono mb-6" style={{ color: 'rgba(139,105,20,0.5)', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>All Terms — Quick Reference ({GLOSSARY_TERMS.length} total)</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {GLOSSARY_TERMS.map(term => (
                  <button key={term.id} onClick={() => { setActiveId(term.id); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    className="p-5 text-left transition-all duration-200 rounded-lg"
                    style={{ background: 'rgba(6,13,18,0.6)', border: `1px solid ${CAT_COLORS[term.category]}20` }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = `${CAT_COLORS[term.category]}50`; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = `${CAT_COLORS[term.category]}20`; }}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-mono font-black" style={{ color: '#F5F0E8', fontSize: '0.85rem' }}>{term.term}</span>
                      <span style={{ fontFamily: "'Amiri', serif", fontSize: 18, color: '#D4A82A', direction: 'rtl' }}>{term.arabic}</span>
                    </div>
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="font-mono" style={{ color: `${CAT_COLORS[term.category]}80`, fontSize: '0.6rem' }}>{CAT_LABELS[term.category]}</span>
                      {term.keyTag && (
                        <span className="font-mono px-1" style={{ background: 'rgba(212,168,42,0.08)', color: 'rgba(212,168,42,0.4)', border: '1px solid rgba(212,168,42,0.12)', fontSize: '0.55rem' }}>{term.keyTag}</span>
                      )}
                    </div>
                    <p className="font-mono mb-1" style={{ color: 'rgba(179,138,42,0.4)', fontSize: '0.6rem' }}>Root: {term.root}</p>
                    <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 11, lineHeight: 1.6 }}>{term.definition.slice(0, 90)}...</p>
                  </button>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}
